import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Camera, RotateCcw, X, Check, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { uploadSingleNew, projectKey, secretKey } from '../../constant/Constant';
import { getAcessToken } from '../../storage/Storage';
import { setApiJson } from '../../redux/Actions/ApiAction';
import { HitApiFormData } from '../../Api/ApiHit';

// Redux action to save file URL
const saveFileUrl = (name, url, fileInfo) => ({
    type: 'SAVE_FILE_URL',
    payload: { name, url, fileInfo }
});

function LivePhotoUpload({ title, name, quality = 0.8 }) {
    const dispatch = useDispatch();
    const ApiReducer = useSelector(state => state.ApiReducer);
    
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [capturedPhoto, setCapturedPhoto] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState(null);
    const [cameraError, setCameraError] = useState(null);
    const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
    const [stream, setStream] = useState(null);
    
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    // Get current file URL from Redux if exists
    const currentFileUrl = ApiReducer?.fileUrls?.[name];
    const apiJsonFileUrl = ApiReducer?.apiJson?.[name];

    // Helper function to extract filename from URL
    const getFileNameFromUrl = (url) => {
        if (!url) return null;
        try {
            const urlParts = url.split('/');
            return urlParts[urlParts.length - 1] || null;
        } catch (error) {
            return null;
        }
    };

    // Initialize camera
    const startCamera = async () => {
        try {
            setCameraError(null);
            setError(null);

            // Stop any existing stream
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }

            const constraints = {
                video: {
                    facingMode: facingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                }
            };

            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(mediaStream);
            
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                videoRef.current.play();
            }
            
            setIsCameraOpen(true);
        } catch (err) {
            console.error('Camera access error:', err);
            setCameraError('Unable to access camera. Please check permissions.');
            setIsCameraOpen(false);
        }
    };

    // Stop camera
    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        setIsCameraOpen(false);
        setCameraError(null);
    };

    // Switch between front and back camera
    const switchCamera = () => {
        setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    };

    // Effect to restart camera when facing mode changes
    useEffect(() => {
        if (isCameraOpen) {
            startCamera();
        }
    }, [facingMode]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
    }, [stream]);

    // Capture photo from video stream
    const capturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob
        canvas.toBlob((blob) => {
            if (blob) {
                const photoUrl = URL.createObjectURL(blob);
                setCapturedPhoto({
                    blob: blob,
                    url: photoUrl,
                    timestamp: new Date().toISOString()
                });
                stopCamera();
            }
        }, 'image/jpeg', quality);
    };

    // Upload captured photo
    const uploadPhoto = async () => {
        if (!capturedPhoto?.blob) return;

        setIsUploading(true);
        setError(null);

        try {
            // Create a File object from blob
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const fileName = `live-photo-${timestamp}.jpg`;
            const file = new File([capturedPhoto.blob], fileName, { type: 'image/jpeg' });

            const result = await uploadSingleFile(file);

            if (result.success && result.data?.file?.s3Url) {
                // Create file info object
                const fileInfo = {
                    name: file.name,
                    size: formatFileSize(file.size),
                    type: file.type,
                    uploadedAt: new Date().toISOString()
                };

                // Get file URL from new API response format
                const fileUrl = result.data.file.s3Url;

                // Save to Redux using saveFileUrl action (consistent with FileuploadComp)
                dispatch(saveFileUrl(name, fileUrl, fileInfo));

                // Clear captured photo after successful upload
                setCapturedPhoto(null);
            } else {
                setError(result.error || 'Upload failed');
            }

        } catch (error) {
            setError('Failed to upload photo. Please try again.');
            console.error('Upload error:', error);
        } finally {
            setIsUploading(false);
        }
    };

    // Upload function - matching FileuploadComp
    const uploadSingleFile = async (file) => {
        try {
            const token = getAcessToken();

            // Prepare data object for new upload API
            const uploadData = {
                file: file,
                userId: secretKey,
                projectKey: projectKey
            };

            console.log("Upload data:", uploadData);

            const response = await HitApiFormData(
                uploadData,
                uploadSingleNew,     // URL - new upload endpoint
                'POST',
                { 'Authorization': `Bearer ${token}` }
            );

            console.log("response", response);

            if (response?.success && response?.file?.s3Url) {
                // Get file URL from new API response format
                const fileUrl = response.file.s3Url;

                let oldJson = ApiReducer?.apiJson || {};
                let newJson = {
                    ...oldJson,
                    [name]: fileUrl
                };
                dispatch(setApiJson(newJson));
            }

            return { success: true, data: response };

        } catch (error) {
            console.error('File upload error:', error);
            return { success: false, error: error.message };
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Retake photo
    const retakePhoto = () => {
        if (capturedPhoto?.url) {
            URL.revokeObjectURL(capturedPhoto.url);
        }
        setCapturedPhoto(null);
        setError(null);
    };

    // Remove uploaded photo
    const removePhoto = () => {
        // Clear from Redux
        dispatch(saveFileUrl(name, null, null));
        
        // Clear from apiJson in Redux
        let oldJson = ApiReducer?.apiJson || {};
        let newJson = { ...oldJson };
        newJson[name] = "";
        dispatch(setApiJson(newJson));
        
        setError(null);
    };

    // Check if there's an existing uploaded photo
    const existingPhoto = currentFileUrl || apiJsonFileUrl;

    return (
        <div className="w-full">
            {title && (
                <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                        {title}
                    </label>
                </div>
            )}
            
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-red-700 text-sm">{error}</span>
                </div>
            )}

            {cameraError && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-yellow-500" />
                    <span className="text-yellow-700 text-sm">{cameraError}</span>
                </div>
            )}

            {/* Existing Photo Display */}
            {existingPhoto && !capturedPhoto && !isCameraOpen && (
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                    <div className="relative">
                        <img
                            src={existingPhoto}
                            alt="Uploaded photo"
                            className="w-full h-48 object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                        <button
                            onClick={removePhoto}
                            className="absolute top-2 right-2 p-1 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full shadow-sm transition-all"
                        >
                            <X className="w-4 h-4 text-gray-600 hover:text-red-500" />
                        </button>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-white">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 rounded-full bg-green-100">
                                <Camera className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                                <p className="font-medium text-gray-800 text-sm">
                                    {getFileNameFromUrl(existingPhoto) || 'Live Captured Photo'}
                                </p>
                                <p className="text-xs text-gray-500">Photo uploaded successfully</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-1 text-green-600">
                            <Check className="w-4 h-4" />
                        </div>
                    </div>
                </div>
            )}

            {/* Camera Interface - Fullscreen */}
            {isCameraOpen && (
                <div className="fixed inset-0 z-50 bg-black">
                    <div className="relative w-full h-full">
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            className="w-full h-full object-cover"
                        />
                        
                        {/* Header Controls */}
                        <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-4">
                            <div className="flex justify-between items-center">
                                <h3 className="text-white font-medium text-lg">
                                    {title || 'Capture Photo'}
                                </h3>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={switchCamera}
                                        className="p-3 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-all"
                                        title="Switch Camera"
                                    >
                                        <RefreshCw className="w-5 h-5 text-white" />
                                    </button>
                                    <button
                                        onClick={stopCamera}
                                        className="p-3 bg-white/20 hover:bg-white/30 rounded-full backdrop-blur-sm transition-all"
                                        title="Close Camera"
                                    >
                                        <X className="w-5 h-5 text-white" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Bottom Controls */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                            <div className="flex justify-center items-center">
                                <button
                                    onClick={capturePhoto}
                                    className="w-20 h-20 bg-white border-4 border-white/30 rounded-full flex items-center justify-center hover:scale-105 transition-transform shadow-2xl"
                                >
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                                        <Camera className="w-8 h-8 text-gray-600" />
                                    </div>
                                </button>
                            </div>
                            
                            {/* Instructions */}
                            <div className="text-center mt-4">
                                <p className="text-white/80 text-sm">
                                    Tap the capture button to take photo
                                </p>
                                <p className="text-white/60 text-xs mt-1">
                                    Position your subject in the center of the frame
                                </p>
                            </div>
                        </div>

                        {/* Camera Guidelines (Optional) */}
                        <div className="absolute inset-0 pointer-events-none">
                            <div className="w-full h-full flex items-center justify-center">
                                <div className="w-64 h-64 border-2 border-white/30 rounded-lg"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Captured Photo Preview */}
            {capturedPhoto && (
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-4">
                    <div className="relative">
                        <img
                            src={capturedPhoto.url}
                            alt="Captured photo"
                            className="w-full h-48 object-cover"
                        />
                    </div>
                    <div className="p-4 bg-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="font-medium text-gray-800 text-sm">Photo Captured</p>
                                <p className="text-xs text-gray-500">Ready to upload</p>
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={retakePhoto}
                                    className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                                    disabled={isUploading}
                                >
                                    <RotateCcw className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={uploadPhoto}
                                    disabled={isUploading}
                                    className="px-4 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-1"
                                >
                                    {isUploading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <>
                                            <Check className="w-4 h-4" />
                                            <span>Upload</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Camera Trigger Button */}
            {!isCameraOpen && !capturedPhoto && !existingPhoto && (
                <button
                    onClick={startCamera}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 hover:bg-gray-50 transition-all duration-300"
                >
                    <div className="flex flex-col items-center space-y-3">
                        <div className="p-3 rounded-full bg-blue-100">
                            <Camera className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <p className="text-sm text-gray-600 mb-1">
                                <span className="font-medium">Open Camera</span>
                            </p>
                            <p className="text-xs text-gray-400">
                                Capture photo directly from camera
                            </p>
                        </div>
                    </div>
                </button>
            )}

            {/* Take New Photo Button (when photo already exists) */}
            {existingPhoto && !isCameraOpen && !capturedPhoto && (
                <button
                    onClick={startCamera}
                    className="w-full mt-3 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                >
                    <Camera className="w-4 h-4" />
                    <span>Take New Photo</span>
                </button>
            )}

            {/* Hidden canvas for photo capture */}
            <canvas ref={canvasRef} className="hidden" />
        </div>
    );
}

export default LivePhotoUpload;